import { useEffect, useState } from 'react';
import { Button, InfoBox } from '@immo/ui-kit';
import { useIntl } from 'react-intl';
import {
  FLEET_LOG_READ_LIMITS,
  type FleetLogLineStream,
  type FleetLogReadLimit,
  type ReadFleetLogResponse,
} from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { CheckIcon, CopyIcon, RefreshIcon, TrashIcon } from '@/components/layout/icons';
import { Segments } from './segments';

type Props = {
  readonly readOnly: boolean;
  readonly stream: FleetLogLineStream;
  readonly limit: FleetLogReadLimit;
  /** Rows the page read from the API, oldest first. `null` before the first read has resolved. */
  readonly log: ReadFleetLogResponse | null;
  readonly loading: boolean;
  readonly onStreamChange: (stream: FleetLogLineStream) => void;
  readonly onLimitChange: (limit: FleetLogReadLimit) => void;
  readonly onRefresh: () => void;
  /** Drops this worker's stored lines older than 24h. Absent when the operator may not command. */
  readonly onCleanLogs?: () => void;
};

/**
 * The two streams the fleet stores and the dashboard reads (IMM-918).
 *
 * `event` is deliberately absent: it is reserved for structured run events and
 * has no lines yet, so offering the tab would only ever show an empty block.
 */
const STREAMS: ReadonlyArray<FleetLogLineStream> = ['agent', 'build'];

/** How long the copy button stays in its confirmed state before reverting. */
const COPIED_FEEDBACK_MS = 2000;

const STREAM_LABEL_KEYS: Record<'agent' | 'build', string> = {
  agent: 'stream-agent',
  build: 'stream-build',
};

/**
 * Stream selector, depth selector, and the lines themselves — read from the
 * fleet's stored rows, never by commanding a machine (IMM-918).
 *
 * That is the whole change from IMM-910.8: a log view used to be a `logs`
 * command that a reachable host answered, so it showed nothing for a machine
 * that was asleep, had no tmux session, or acknowledged and never replied —
 * which is precisely when somebody opens it. The fleet agent now pushes lines
 * on every tick, so this reads what is already here.
 *
 * Still never touches `apiClient`: the page owns the read and passes it down.
 */
export function LogViewer({
  readOnly,
  stream,
  limit,
  log,
  loading,
  onStreamChange,
  onLimitChange,
  onRefresh,
  onCleanLogs,
}: Props) {
  const t = useTranslations('pages.admin.fleet.log');
  const tFleet = useTranslations('pages.admin.fleet');
  const [copiedAt, setCopiedAt] = useState<number | null>(null);

  // The one source for the block's text: the toolbar copies precisely what the
  // <pre> renders. A second `join` up here would drift from the body's without
  // anything failing — the clipboard would just quietly stop matching the
  // screen.
  const text = log === null ? '' : log.lines.map((line) => line.text).join('\n');

  useEffect(() => {
    if (copiedAt === null) return;

    const timer = setTimeout(() => setCopiedAt(null), COPIED_FEEDBACK_MS);

    return () => clearTimeout(timer);
  }, [copiedAt]);

  const copied = copiedAt !== null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      // A timestamp rather than a flag: copying again inside the window has to
      // restart it, and a `true` that is already `true` re-runs no effect.
      setCopiedAt(Date.now());
    } catch {
      // Clipboard access is gated by secure context + user activation and can
      // be denied; the block stays on screen to be selected by hand.
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-md border border-border bg-canvas p-3">
      {/* Below `sm` the toolbar stacks: each Segments group owns a full row
          (see segments.tsx), and the refresh control sits beside the depth
          rather than being pushed to a right edge that does not exist. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
        {readOnly ? (
          <span className="font-mono text-base-sm text-text-primary">
            {t(STREAM_LABEL_KEYS[stream === 'event' ? 'agent' : stream])}
          </span>
        ) : (
          <Segments
            ariaLabel={t('stream-group')}
            value={stream}
            onChange={onStreamChange}
            options={STREAMS.map((value) => ({ value, label: t(STREAM_LABEL_KEYS[value as 'agent' | 'build']) }))}
          />
        )}

        {!readOnly && (
          <Segments
            ariaLabel={t('lines')}
            value={limit}
            onChange={onLimitChange}
            options={FLEET_LOG_READ_LIMITS.map((value) => ({ value, label: String(value) }))}
          />
        )}

        <span className="hidden flex-1 sm:block" />

        {/* All three are icons with an accessible name rather than labelled
            buttons: they sit beside the two Segments groups, and three blocks
            of words in one toolbar left nothing reading as the primary
            control. Ordered by consequence — copy reads, refresh replaces,
            clean destroys — so the destructive one is furthest from the
            pointer's resting place. The clean keeps its danger tone: it
            destroys rows, and an unlabelled destructive icon needs the colour
            to say so. */}
        {!readOnly && (
          <div className="flex items-center gap-1">
            {/* Absent rather than disabled when there is nothing to copy. A
                disabled control carries its reason in a `title` that
                `disabled:pointer-events-none` then makes unreachable (BL-061)
                — and the reason is already on screen, where the empty notice
                has replaced the block this would have copied. */}
            {text !== '' && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={copied ? t('copied') : t('copy')}
                title={copied ? t('copied') : t('copy')}
                onClick={() => void copy()}
              >
                {copied ? <CheckIcon className="h-4 w-4" aria-hidden /> : <CopyIcon className="h-4 w-4" aria-hidden />}
              </Button>
            )}

            <Button
              type="button"
              size="sm"
              variant="ghost"
              aria-label={loading ? t('fetching') : t('fetch-again')}
              title={loading ? t('fetching') : t('fetch-again')}
              onClick={onRefresh}
              disabled={loading}
            >
              <RefreshIcon className="h-4 w-4" aria-hidden />
            </Button>

            {onCleanLogs !== undefined && (
              <Button
                type="button"
                size="sm"
                variant="danger-quiet"
                aria-label={tFleet('clean-logs')}
                title={tFleet('clean-logs')}
                onClick={onCleanLogs}
              >
                <TrashIcon className="h-4 w-4" aria-hidden />
              </Button>
            )}
          </div>
        )}
      </div>

      <LogViewerBody log={log} loading={loading} text={text} />
    </div>
  );
}

function LogViewerBody({
  log,
  loading,
  text,
}: {
  readonly log: ReadFleetLogResponse | null;
  readonly loading: boolean;
  /** The block's text, built by the parent so the copy action cannot drift from it. */
  readonly text: string;
}) {
  const t = useTranslations('pages.admin.fleet.log');
  const intl = useIntl();

  if (log === null) return loading ? <span className="text-sm text-text-tertiary">{t('fetching')}</span> : null;

  if (log.lines.length === 0) {
    return <InfoBox type="info">{`${t('empty-title')} ${t('empty-body')}`}</InfoBox>;
  }

  const newest = log.lines[log.lines.length - 1];

  return (
    <div className="flex flex-col gap-2">
      {/* `whitespace-pre` + `overflow-auto` is load-bearing, not incidental:
          a wrapped build log gives up the column the timestamps live in and
          turns 50 lines into ~130. At 390px the block scrolls sideways.

          `selection:` is what makes dragging across the block *look* like it
          does something. Nothing suppresses selection here — the ui-kit's one
          global `::selection` paints `rgba(17,24,39,0.12)`, a 12% black wash
          sized for the light surfaces the rest of the admin app is built
          from, and sets no `color`. This is the only dark surface in the app,
          so that wash over `bg-neutral-900` is no visible change and the text
          does not invert either: the selection exists and is invisible, which
          reads as a block you are not allowed to select. Overridden here
          rather than in the token, which is right everywhere else. */}
      <pre className="max-h-[320px] overflow-auto whitespace-pre rounded-sm bg-neutral-900 p-3 font-mono text-xs text-neutral-50 selection:bg-neutral-50/30 sm:max-h-[420px] sm:text-sm">
        {text}
      </pre>

      <div className="flex flex-wrap items-center gap-2">
        {newest !== undefined && (
          <span className="text-sm text-text-tertiary">
            {t('newest-at', {
              time: intl.formatDate(newest.at, { dateStyle: 'short', timeStyle: 'short' }),
            })}
          </span>
        )}
        <span className="text-xs text-text-placeholder sm:hidden">{t('scroll-hint')}</span>
      </div>
    </div>
  );
}
